import { SalesTaxFilingFrequencyEnum } from '@prisma/client';
import { useMemo } from 'react';
import { FILING_FREQUENCY_CONFIG, VALIDATION_MESSAGES } from '../constants';
import {
  CalendarPreviewValidationError,
  FilingCalendarPreviewData,
  MonthYearValue,
} from '../types';
import { CalendarPreviewHelper } from '../helpers';

export interface UseCalendarValidationParams {
  previewData: FilingCalendarPreviewData | null | undefined;
  state: string;
  taxPeriodStart: MonthYearValue | null;
  taxPeriodEnd: MonthYearValue | null;
  filingFrequency: SalesTaxFilingFrequencyEnum | null;
}

export interface UseCalendarValidationResult {
  // Validation states
  hasFiledConflict: boolean;
  hasCoverageGaps: boolean;
  hasValidationErrors: boolean;
  canGenerate: boolean;

  // Error messages
  formErrors: CalendarPreviewValidationError[];
  apiErrors: string[];

  // Specific error checks
  isDateRangeTooShort: boolean;
  isEndBeforeStart: boolean;
  isMissingRequiredFields: boolean;

  // Combined error message for display
  primaryErrorMessage: string | null;
}

export function useCalendarValidation({
  previewData,
  state,
  taxPeriodStart,
  taxPeriodEnd,
  filingFrequency,
}: UseCalendarValidationParams): UseCalendarValidationResult {
  return useMemo(() => {
    const formErrors: CalendarPreviewValidationError[] = [];
    const apiErrors: string[] = previewData?.validation_errors || [];

    // Check required fields
    const isMissingRequiredFields =
      !state || !taxPeriodStart || !filingFrequency;

    if (!state) {
      formErrors.push({
        field: 'state',
        message: VALIDATION_MESSAGES.STATE_REQUIRED,
      });
    }

    if (!taxPeriodStart) {
      formErrors.push({
        field: 'taxPeriodStart',
        message: VALIDATION_MESSAGES.TAX_PERIOD_START_REQUIRED,
      });
    }

    if (!filingFrequency) {
      formErrors.push({
        field: 'filingFrequency',
        message: VALIDATION_MESSAGES.FILING_FREQUENCY_REQUIRED,
      });
    }

    // Check date range validity
    let isEndBeforeStart = false;
    let isDateRangeTooShort = false;

    if (taxPeriodStart && taxPeriodEnd) {
      const startValue = taxPeriodStart.year * 12 + taxPeriodStart.month;
      const endValue = taxPeriodEnd.year * 12 + taxPeriodEnd.month;

      if (endValue < startValue) {
        isEndBeforeStart = true;
        formErrors.push({
          field: 'taxPeriodEnd',
          message: VALIDATION_MESSAGES.END_BEFORE_START,
        });
      } else if (filingFrequency) {
        const frequencyConfig = FILING_FREQUENCY_CONFIG.find(
          (f) => f.value === filingFrequency
        );
        if (frequencyConfig) {
          const monthsDiff = CalendarPreviewHelper.getMonthsDifference(
            taxPeriodStart,
            taxPeriodEnd
          );
          if (monthsDiff < frequencyConfig.minMonths) {
            isDateRangeTooShort = true;
            formErrors.push({
              field: 'general',
              message: VALIDATION_MESSAGES.DATE_RANGE_TOO_SHORT(
                frequencyConfig.label,
                frequencyConfig.minMonths
              ),
            });
          }
        }
      }
    }

    // API-level validation
    const hasFiledConflict = previewData?.has_filed_conflict ?? false;
    const hasCoverageGaps = previewData?.has_coverage_gaps ?? false;
    const hasValidationErrors = apiErrors.length > 0;
    const canGenerate = previewData?.can_generate ?? false;

    // Determine primary error message
    let primaryErrorMessage: string | null = null;

    if (hasFiledConflict) {
      primaryErrorMessage = VALIDATION_MESSAGES.FILED_CONFLICT;
    } else if (hasCoverageGaps) {
      primaryErrorMessage = VALIDATION_MESSAGES.COVERAGE_GAP;
    } else if (formErrors.length > 0) {
      primaryErrorMessage = formErrors[0].message;
    } else if (apiErrors.length > 0) {
      primaryErrorMessage = apiErrors[0] ?? null;
    }

    return {
      hasFiledConflict,
      hasCoverageGaps,
      hasValidationErrors,
      canGenerate,
      formErrors,
      apiErrors,
      isDateRangeTooShort,
      isEndBeforeStart,
      isMissingRequiredFields,
      primaryErrorMessage,
    };
  }, [previewData, state, taxPeriodStart, taxPeriodEnd, filingFrequency]);
}
